import { Link, useNavigate } from 'react-router-dom'
import Seo from '@/components/Seo'
import SiteLayout from '@/components/SiteLayout'
import PageHero from '@/components/PageHero'
import CtaBand from '@/components/CtaBand'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Check, Smartphone, Infinity, Layers } from 'lucide-react'
import { pricingPage } from '@/content/pricing'
import { heroImageOf } from '@/content/applyImages'

function PricingPage() {
  const navigate = useNavigate()

  const platformFeatures = [
    'Point of Sale System',
    'Order Management',
    'Team Management',
    'Time Tracking',
    'Menu Management',
    'Analytics & Reports',
    'Table Management',
    'Customer-Facing Display',
    '24/7 Support',
  ]

  const paymentFeatures = [
    'Accept Any Card',
    '1.1% Tillio flat rate',
    'Plus Stripe processing rates',
    'End to End Encryption',
    'PCI Compliance',
    'On-demand Support',
    'Dispute Management',
    'Chip, Swipe, and Tap',
  ]

  const pricingFaqs = [
    {
      q: 'Are there setup fees?',
      a: 'No. There are no setup fees to start with Tillio POS. Hardware is optional and purchased as needed.',
    },
    {
      q: 'What is included in the $99/mo platform fee?',
      a: 'POS, orders, tables, team management, time tracking, menu tools, analytics, and support—with unlimited device usage for your location.',
    },
    {
      q: 'How do card payments work?',
      a: (
        <>
          Tillio adds a flat 1.1% on card payments. Stripe also charges its own processing rates—see{' '}
          <a
            href="https://stripe.com/pricing#payments"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Stripe pricing
          </a>
          . Card data is handled by Stripe, not stored by Tillio.
        </>
      ),
    },
    {
      q: 'Can I use multiple tablets?',
      a: 'Yes. Sync as many devices as you need without per-device software fees.',
    },
  ]

  return (
    <SiteLayout>
      <Seo
        title={pricingPage.seoTitle}
        description={pricingPage.seoDescription}
        path={pricingPage.path}
        image={heroImageOf(pricingPage)}
      />

      <PageHero
        title={pricingPage.hero.title}
        description={pricingPage.hero.description}
        image={heroImageOf(pricingPage)}
        imageAlt="Tillio POS pricing"
        eyebrow="Simple pricing"
        showCta={false}
      />

      <section className="py-20 bg-background">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="border-2 border-primary shadow-lg">
              <CardHeader className="text-center pb-8">
                <CardDescription className="text-lg mb-2">Platform</CardDescription>
                <div className="flex items-baseline justify-center gap-2 mb-4">
                  <span className="text-sm text-muted-foreground">Starting at</span>
                  <CardTitle className="text-5xl font-bold">$99</CardTitle>
                  <span className="text-muted-foreground">/mo</span>
                </div>
                <Button size="lg" className="w-full" onClick={() => navigate('/onboarding')}>
                  Get Started
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {platformFeatures.map((feature) => (
                    <div key={feature} className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-primary shrink-0" />
                      <span className="text-muted-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 shadow-lg">
              <CardHeader className="text-center pb-8">
                <CardDescription className="text-lg mb-2">Payments</CardDescription>
                <div className="mb-4">
                  <div className="text-3xl font-bold mb-1">1.1%</div>
                  <div className="text-sm text-muted-foreground mt-2">Tillio flat rate</div>
                  <p className="text-sm text-muted-foreground mt-3 max-w-xs mx-auto leading-relaxed">
                    Plus{' '}
                    <a
                      href="https://stripe.com/pricing#payments"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Stripe&apos;s processing rates
                    </a>
                  </p>
                </div>
                <Button size="lg" variant="outline" className="w-full" onClick={() => navigate('/onboarding')}>
                  Get Started
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {paymentFeatures.map((feature) => (
                    <div key={feature} className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-primary shrink-0" />
                      <span className="text-muted-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mt-6">
                  Learn more on our{' '}
                  <Link to="/stripe" className="text-primary hover:underline">Stripe integration</Link>
                  {', '}
                  <Link to="/features/payments" className="text-primary hover:underline">payments feature page</Link>
                  {' '}and{' '}
                  <Link to="/hardware" className="text-primary hover:underline">hardware</Link>.
                </p>
              </CardContent>
            </Card>
          </div>
          <p className="text-center mt-8 text-muted-foreground">24/7 Support · Dedicated Onboarding</p>
        </div>
      </section>

      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {[
              { icon: Smartphone, title: 'No setup fees.', text: 'Get started without upfront software costs' },
              { icon: Infinity, title: 'Unlimited usage.', text: 'No per-transaction software limits' },
              { icon: Layers, title: 'One platform.', text: 'POS, team, menu, and analytics together' },
            ].map((item) => (
              <div key={item.title} className="flex flex-col items-center">
                <div className="h-16 w-16 rounded-lg bg-white/10 flex items-center justify-center mb-4">
                  <item.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2">{item.title}</h3>
                <p className="text-white/70">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto max-w-3xl px-6">
          <h2 className="text-3xl font-bold text-center mb-10">Pricing FAQ</h2>
          <Accordion>
            {pricingFaqs.map((faq, index) => (
              <AccordionItem key={faq.q} index={index}>
                <AccordionTrigger>{faq.q}</AccordionTrigger>
                <AccordionContent>
                  <p>{faq.a}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <p className="text-center mt-8 text-muted-foreground">
            More answers on our <Link to="/faq" className="text-primary hover:underline">FAQ page</Link>.
          </p>
        </div>
      </section>

      <CtaBand title="Ready to get started?" description="Tell us about your restaurant and we’ll help you launch Tillio POS." />
    </SiteLayout>
  )
}

export default PricingPage
